<template>
  <div class="m-company">
    <van-nav-bar title="企业信息" left-arrow @click-left="$router.back()" fixed placeholder />

    <!-- 骨架屏 -->
    <div v-if="loading" class="m-company__skeleton">
      <div class="m-company__skeleton-avatar-section">
        <div class="m-company__skeleton-avatar"></div>
        <div class="m-company__skeleton-hint"></div>
        <div class="m-company__skeleton-code"></div>
      </div>
      <div class="m-company__skeleton-title"></div>
      <div class="m-company__skeleton-cell-group"><div class="m-company__skeleton-cell"></div></div>
      <div class="m-company__skeleton-title"></div>
      <div class="m-company__skeleton-cell-group"><div class="m-company__skeleton-cell"></div><div class="m-company__skeleton-cell"></div></div>
      <div class="m-company__skeleton-title"></div>
      <div class="m-company__skeleton-cell-group"><div class="m-company__skeleton-cell"></div><div class="m-company__skeleton-cell"></div><div class="m-company__skeleton-cell"></div><div class="m-company__skeleton-cell"></div><div class="m-company__skeleton-cell"></div></div>
    </div>

    <template v-else>
    <!-- Logo 区 -->
    <div class="m-company__avatar-section">
      <!-- 第一块：企业信息 -->
      <div class="personal-info-avatar-wrap">
        <!-- Logo 显示容器 - 包含 Logo 和审核标签 -->
        <div 
          v-if="avatarDisplay.auditStatus !== 1 && avatarDisplay.auditStatus !== 3" 
          class="m-company__avatar personal-info-avatar" 
          @click="triggerAvatarUpload"
        >
          <img :src="company.avatar || ''" alt="avatar" />
          <van-icon v-if="!company.avatar" name="shop-o" size="40" color="#ccc" />
          <div class="m-company__avatar-mask personal-info-avatar-mask">
            <span>修改</span>
          </div>
        </div>
        <!-- 审核中和驳回状态 - 不可点击 -->
        <div 
          v-if="(avatarDisplay.auditStatus === 1 || avatarDisplay.auditStatus === 3)"
          class="m-company__avatar personal-info-avatar personal-info-avatar--readonly"
        >
          <img :src="avatarDisplay.src" alt="avatar" />
          <van-icon v-if="!avatarDisplay.src" name="shop-o" size="40" color="#ccc" />
          <!-- 审核中/驳回提示（在 Logo 顶部） -->
          <div v-if="avatarDisplay.auditStatus === 1" class="personal-avatar-audit-tip personal-avatar-tag-pending">审核中</div>
          <!-- 驳回提示（在 Logo 顶部） -->
          <div v-if="avatarDisplay.auditStatus === 3" class="personal-avatar-audit-tip personal-avatar-tag-rejected">驳回</div>
        </div>
      </div>
      <input ref="avatarInput" type="file" accept="image/*" style="display:none" @change="onAvatarChange" />
      <div class="m-company__code">企业编号：{{ companyCode || '--' }}</div>
    </div>

    <!-- 基本信息 -->
    <div class="m-company__section-title">基本信息</div>
    <van-cell-group class="m-company__group">
      <van-cell title="企业简称" :value="company.shortName" is-link @click="openShortNameDialog" />
    </van-cell-group>

    <!-- 登录信息 -->
    <div class="m-company__section-title">登录信息</div>
    <van-cell-group class="m-company__group">
      <van-cell title="绑定手机" :value="loginInfo.mobile" is-link @click="openMobileDialog" />
      <van-cell title="登录密码" value="********" is-link @click="openPasswordDialog" />
    </van-cell-group>

    <!-- 企业认证 -->
    <div class="m-company__section-title">企业认证信息</div>
    <van-cell-group class="m-company__group">
      <van-cell title="认证状态">
        <template #right-icon>
          <van-tag :type="authTagType" size="medium">{{ authStatusText }}</van-tag>
        </template>
      </van-cell>
      <div v-if="authStatus === 3 && auditReason" class="m-company__auth-reason">
        失败原因：{{ auditReason }}
      </div>
      <van-cell title="行业" :value="authInfo.industry || '--'" />
      <van-cell title="企业名称" :value="authInfo.companyName || '--'" />
      <van-cell title="法人姓名" :value="authInfo.legalName || '--'" />
      <van-cell title="法人电话" :value="authInfo.legalPhone || '--'" />
      <div v-if="authInfo.license" class="m-company__license-preview">
        <img :src="authInfo.license" alt="营业执照" @click="previewImage(authInfo.license)" />
      </div>
      <div v-if="authStatus === 0 || authStatus === 3" class="m-company__auth-action">
        <van-button type="primary" size="small" round @click="openAuthDialog">修改认证信息</van-button>
      </div>
    </van-cell-group>
    </template>

    <!-- 修改简称弹窗 -->
    <van-dialog v-model="showShortNameDialog" title="修改企业简称" show-cancel-button @confirm="saveShortName">
      <van-field v-model="form.shortName" placeholder="请输入企业简称" @input="handleShortNameInput" class="m-company__dialog-field" />
    </van-dialog>

    <!-- 修改手机号弹窗 -->
    <van-dialog
      v-model="showMobileDialog"
      title="修改手机号码"
      show-cancel-button
      :before-close="saveMobile"
      @cancel="showMobileDialog = false"
    >
      <div class="m-company__dialog-content">
        <div class="m-company__code-row" style="margin-bottom: 12px;">
          <span style="color: #666; font-size: 14px;">登录手机号：</span>
          <span style="color: #333; font-size: 14px;">{{ loginInfo.mobile }}</span>
        </div>
        <van-field v-model="mobileForm.newMobile" placeholder="请输入新手机号" type="tel" maxlength="11" left-icon="phone-o" />
        <van-field v-model="mobileForm.confirmNewMobile" placeholder="请再次输入新手机号" type="tel" maxlength="11" left-icon="phone-o" />
        <div class="m-company__code-row">
          <van-field v-model="mobileForm.captchaCode" placeholder="请输入图形验证码" maxlength="6" left-icon="shield-o" class="m-company__code-field" />
          <div style="width: 80px; height: 36px; flex-shrink: 0; border-radius: 4px; overflow: hidden; background: #f5f5f5; cursor: pointer;" @click="refreshBindingCaptcha">
            <img v-if="bindingCaptchaUrl" :src="bindingCaptchaUrl" alt="图形验证码" style="width: 100%; height: 100%; object-fit: cover;" />
            <span v-else style="font-size: 11px; color: #666; display: flex; align-items: center; justify-content: center; height: 100%;">点击获取</span>
          </div>
        </div>
        <div class="m-company__code-row">
          <van-field v-model="mobileForm.smsCode" placeholder="请输入短信验证码" type="digit" maxlength="6" left-icon="envelop-o" class="m-company__code-field" />
          <van-button plain type="primary" size="small" :disabled="countdown > 0" @click="handleSendMobileCode" class="m-company__code-btn">
            {{ countdown > 0 ? `${countdown}s` : '获取验证码' }}
          </van-button>
        </div>
      </div>
    </van-dialog>

    <!-- 修改密码弹窗 -->
    <van-dialog
      v-model="showPasswordDialog"
      title="修改密码"
      show-cancel-button
      :before-close="savePassword"
      @cancel="showPasswordDialog = false"
    >
      <div class="m-company__dialog-content">
        <div class="m-company__code-row">
          <van-field v-model="passwordForm.captchaInput" placeholder="请输入图形验证码" maxlength="6" left-icon="shield-o" class="m-company__code-field" />
          <div style="width: 80px; height: 36px; flex-shrink: 0; border-radius: 4px; overflow: hidden; background: #f5f5f5; cursor: pointer;" @click="refreshPasswordCaptcha">
            <img v-if="passwordCaptchaUrl" :src="passwordCaptchaUrl" alt="图形验证码" style="width: 100%; height: 100%; object-fit: cover;" />
            <span v-else style="font-size: 11px; color: #666; display: flex; align-items: center; justify-content: center; height: 100%;">点击获取</span>
          </div>
        </div>
        <div class="m-company__code-row">
          <van-field v-model="passwordForm.smsCode" placeholder="请输入短信验证码" type="digit" maxlength="6" left-icon="envelop-o" class="m-company__code-field" />
          <van-button plain type="primary" size="small" :disabled="passwordCountdown > 0" @click="handleSendPasswordCode" class="m-company__code-btn">
            {{ passwordCountdown > 0 ? `${passwordCountdown}s` : '获取验证码' }}
          </van-button>
        </div>
        <van-field v-model="passwordForm.newPassword" placeholder="请输入新密码" type="password" left-icon="lock" />
        <van-field v-model="passwordForm.confirmPassword" placeholder="请再次输入新密码" type="password" left-icon="lock" />
        <div class="m-company__pwd-hint">密码长度必须大于8位，并且是复杂密码（包含大小写字母、数字、特殊符号中的至少 2 种）</div>
      </div>
    </van-dialog>

    <!-- 企业认证弹窗 -->
    <van-popup v-model="showAuthDialog" position="bottom" round :style="{ maxHeight: '85%' }" closeable>
      <div class="m-company__auth-popup">
        <div class="m-company__auth-popup-title">企业认证信息</div>
        <div style="padding: 12px 16px">
          <van-field v-model="form.industry" label="行业" placeholder="请输入行业" @input="handleIndustryInput" />
          <van-field v-model="form.companyName" label="企业名称" placeholder="请输入企业名称" @input="handleCompanyNameInput" />
          <!-- 营业执照 -->
          <div class="m-company__upload-label">营业执照</div>
          <div class="m-company__upload-box" @click="triggerLicenseUpload">
            <img v-if="form.license" :src="form.license" alt="营业执照" />
            <div v-else class="m-company__upload-placeholder">
              <van-icon name="photograph" size="24" color="#ccc" />
              <span>上传营业执照</span>
            </div>
          </div>
          <input ref="licenseInput" type="file" accept="image/*" style="display:none" @change="onLicenseChange" />
          <!-- 法人信息 -->
          <van-field v-model="form.legalName" label="法人姓名" placeholder="请输入法人姓名" @input="handleLegalNameInput" />
          <van-field v-model="form.legalPhone" label="法人电话" placeholder="请输入法人电话" @input="handleLegalPhoneInput" />
          <!-- 法人身份证 -->
          <div class="m-company__upload-label">法人身份证</div>
          <div class="m-company__idcard-row">
            <div class="m-company__idcard-box" @click="triggerIdCardUpload('front')">
              <img v-if="form.legalIdCardFront" :src="form.legalIdCardFront" alt="正面" />
              <div v-else class="m-company__upload-placeholder">
                <van-icon name="photograph" size="20" color="#ccc" />
                <span>正面</span>
              </div>
            </div>
            <div class="m-company__idcard-box" @click="triggerIdCardUpload('back')">
              <img v-if="form.legalIdCardBack" :src="form.legalIdCardBack" alt="背面" />
              <div v-else class="m-company__upload-placeholder">
                <van-icon name="photograph" size="20" color="#ccc" />
                <span>国徽面</span>
              </div>
            </div>
          </div>
          <input ref="legalIdCardFrontInput" type="file" accept="image/*" style="display:none" @change="onIdCardChange($event, 'front')" />
          <input ref="legalIdCardBackInput" type="file" accept="image/*" style="display:none" @change="onIdCardChange($event, 'back')" />
          <input ref="brandInput" type="file" accept="image/*" style="display:none" @change="onBrandChange" />
          <!-- 品牌 -->
          <div class="m-company__brands-header">
            <span class="m-company__upload-label" style="margin:0">拥有品牌</span>
            <van-button plain type="primary" size="mini" @click="addBrand">+ 新增</van-button>
          </div>
          <div class="m-company__brand-list">
            <div v-for="(brand, index) in form.brands" :key="index" class="m-company__brand-item">
              <div class="m-company__brand-image" @click="triggerBrandUpload(index)">
                <img v-if="brand.image" :src="brand.image" alt="品牌" />
                <van-icon v-else name="photograph" size="20" color="#ccc" />
              </div>
              <van-field v-model="brand.name" placeholder="品牌名称" class="m-company__brand-name" @input="handleBrandNameInput(index)" />
              <van-icon name="cross" color="#FF4D4F" size="16" @click="removeBrand(index)" />
            </div>
          </div>
          <div style="padding: 16px 0">
            <van-button type="primary" block round @click="saveAuthInfo">提交认证</van-button>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Toast, ImagePreview } from 'vant'
import mobileViewport from './mixins/viewport'
import {
  getCompanyInfo,
  updateAvatar,
  updateShortName,
  changePhone,
  changePassword,
  completeCompany
} from '../../api/company'
import request from '../../api/request'
import CryptoJS from 'crypto-js'

export default {
  mixins: [mobileViewport],
  name: 'MobileCompanyInfo',
  data() {
    return {
      company: { avatar: '', shortName: '' },
      // 新 Logo 审核状态相关字段
      newAvatar: '',
      newAvatarAuditStatus: 0,
      companyCode: '',
      loginInfo: { mobile: '', password: '********' },
      authInfo: {
        industry: '', companyName: '', license: '',
        legalName: '', legalPhone: '',
        legalIdCardFront: '', legalIdCardBack: '',
        brands: []
      },
      authStatus: 0,
      auditReason: '',
      showShortNameDialog: false,
      showMobileDialog: false,
      showPasswordDialog: false,
      showAuthDialog: false,
      countdown: 0,
      timer: null,
      currentBrandIndex: -1,
      form: {
        shortName: '', industry: '', companyName: '', license: '',
        legalName: '', legalPhone: '', legalIdCardFront: '',
        legalIdCardBack: '', brands: []
      },
      mobileForm: {
        newMobile: '',
        confirmNewMobile: '',
        captchaCode: '',   // 图形验证码输入框
        smsCode: '',       // 短信验证码输入框
        uuid: ''           // 图形验证码 UUID
      },
      // 图形验证码相关
      bindingCaptchaUrl: '',
      passwordForm: {
        captchaInput: '',   // 图形验证码输入框
        smsCode: '',        // 短信验证码输入框
        newPassword: '',
        confirmPassword: ''
      },
      // 密码图形验证码相关
      passwordCountdown: 0,
      passwordTimer: null,
      passwordCaptchaUrl: '',
      passwordCaptchaUuid: '',
      loading: true
    }
  },
  computed: {
    authStatusText() {
      const map = { 0: '未认证', 1: '待审核', 2: '已认证', 3: '认证失败' }
      return map[this.authStatus] || '未知'
    },
    authTagType() {
      const map = { 0: 'default', 1: 'warning', 2: 'success', 3: 'danger' }
      return map[this.authStatus] || 'default'
    },
    /**
     * Logo 显示逻辑
     * - newAvatarAuditStatus 为 1 时显示 newAvatar（审核中）
     * - newAvatarAuditStatus 为 3 时显示 newAvatar（驳回）
     * - 其他情况显示 company.avatar
     */
    avatarDisplay() {
      const auditStatus = this.newAvatarAuditStatus
      let src = ''
      
      if (auditStatus === 1 || auditStatus === 3) {
        // 审核中和驳回都显示新 Logo
        src = this.newAvatar || ''
      } else {
        // 其他情况显示原 Logo
        src = this.company.avatar || ''
      }
      
      return {
        src,
        auditStatus
      }
    }
  },
  created() {
    this.getCompanyInfo()
  },
  beforeDestroy() {
    this.clearTimer()
    if (this.passwordTimer) {
      clearInterval(this.passwordTimer)
      this.passwordTimer = null
    }
  },
  methods: {
    async getCompanyInfo() {
      try {
        const res = await getCompanyInfo()
        const data = res.data || {}
        this.companyCode = data.companyCode || ''
        this.company = { avatar: data.avatar || '', shortName: data.companyShortName || '' }
        // 新 Logo 审核状态相关字段
        this.newAvatar = data.newAvatar || ''
        this.newAvatarAuditStatus = data.newAvatarAuditStatus || 0
        this.loginInfo = { mobile: data.mobile || '', password: '********' }
        this.authStatus = data.auditStatus || 0
        this.auditReason = data.auditReason || ''
        this.authInfo = {
          industry: data.industry || '',
          companyName: data.companyName || '',
          license: data.businessLicenseImage || '',
          legalName: data.legalPersonName || '',
          legalPhone: data.legalPersonPhone || '',
          legalIdCardFront: data.legalPersonFrontImage || '',
          legalIdCardBack: data.legalPersonBackImage || '',
          brands: (data.companyBrandList || []).map(item => ({
            id: item.id, name: item.brandName || '', image: item.brandImage || ''
          }))
        }
      } catch (err) {
        Toast(err.message || '获取企业信息失败')
      } finally {
        this.loading = false
      }
    },
    previewImage(url) {
      ImagePreview([url])
    },
    // 头像
    triggerAvatarUpload() { this.$refs.avatarInput.click() },
    async onAvatarChange(e) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) { Toast('请选择图片文件'); return }
      try {
        const url = await this.uploadFile(file)
        await updateAvatar(url)
        // 上传后重新获取企业信息，更新 Logo 审核状态
        await this.getCompanyInfo()
        Toast('Logo 上传成功')
      } catch (err) {
        Toast(err.message || 'Logo 上传失败')
      }
    },
    async uploadFile(file) {
      const formData = new FormData()
      formData.append('file', file)
      try {
        const res = await request.post('/api/common/uploadFile/picFile', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return res.data
      } catch (err) {
        console.error('uploadFile error:', err)
        throw err
      }
    },
    // 简称
    openShortNameDialog() {
      this.form.shortName = this.company.shortName
      this.showShortNameDialog = true
    },
    /** 企业简称输入处理：移除特殊字符，限制20字符 */
    handleShortNameInput() {
      const reg = /[^一-\u9fa5a-zA-Z0-9]/g
      this.form.shortName = this.form.shortName.replace(reg, '')
      this.form.shortName = this.limitCharLength(this.form.shortName, 20)
    },
    /** 按字符权重截断字符串（英文数字占 1，汉字占 2） */
    limitCharLength(str, maxLen) {
      if (!str) return str
      let len = 0
      let i = 0
      for (; i < str.length; i++) {
        const charLen = str.charCodeAt(i) > 127 ? 2 : 1
        if (len + charLen > maxLen) break
        len += charLen
      }
      return str.substring(0, i)
    },
    async saveShortName() {
      const shortName = (this.form.shortName || '').trim()
      if (!shortName) { Toast('企业简称不能为空'); return }
      try {
        await updateShortName(shortName)
        this.company.shortName = shortName
        Toast('企业简称修改成功')
      } catch (err) {
        Toast(err.message || '企业简称修改失败')
      }
    },
    // 手机号
    openMobileDialog() {
      this.mobileForm = {
        newMobile: '',
        confirmNewMobile: '',
        captchaCode: '',
        smsCode: '',
        uuid: ''
      }
      this.showMobileDialog = true
      this.loadBindingCaptcha()  // 打开弹窗时立即加载图形验证码
    },
    async saveMobile(action, done) {
      if (action !== 'confirm') { done(); return }
      const newMobile = (this.mobileForm.newMobile || '').trim()
      const confirmNewMobile = (this.mobileForm.confirmNewMobile || '').trim()
      const smsCode = (this.mobileForm.smsCode || '').trim()
      
      if (!newMobile) {
        Toast('请输入新手机号');done(false); return
      }
      if (!/^1[3-9]\d{9}$/.test(newMobile)) {
        Toast('请输入正确的手机号'); done(false); return
      }
      if (!confirmNewMobile) {
        Toast('请确认新手机号');done(false); return
      }
      if (newMobile !== confirmNewMobile) {
        Toast('两次输入的新手机号不一致'); done(false);return
      }
      if (!smsCode) {
        Toast('请输入短信验证码');done(false); return
      }
      try {
        await changePhone({ code: smsCode, newMobile, password: '' })
        this.loginInfo.mobile = newMobile
        Toast('手机号修改成功')
        this.showMobileDialog = false
      } catch (err) {
        Toast(err.message || '手机号修改失败')
        done(false);
      }
    },
    // 密码
    openPasswordDialog() {
      this.passwordForm = {
        captchaInput: '',
        smsCode: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordCountdown = 0
      this.showPasswordDialog = true
      this.loadPasswordCaptcha()  // 打开弹窗时立即加载图形验证码
    },
    async savePassword(action, done) {
      if (action !== 'confirm') { done(); return }
      const { smsCode, newPassword, confirmPassword } = this.passwordForm
      if (!smsCode) { Toast('请输入短信验证码');done(false); return }
      if (!newPassword) { Toast('请输入新密码'); done(false);return }
      
      // 密码验证：长度 8-20 位，且至少包含两种字符类型
      if (newPassword.length < 8 || newPassword.length > 20) {
        Toast('密码长度必须在 8-20 个字符之间')
        done(false);
        return
      }
      
      const hasUpper = /[A-Z]/.test(newPassword)
      const hasLower = /[a-z]/.test(newPassword)
      const hasDigit = /\d/.test(newPassword)
      const hasSpecial = /[!@#$%^&*()_+=\-{}[\];':"\\|,.<>?~`]/.test(newPassword)
      
      const typeCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length
      
      if (typeCount < 2) {
        Toast('密码必须包含至少两种字符类型 (大小写字母、数字、特殊符号)')
        done(false);
        return
      }
      
      if (newPassword !== confirmPassword) {
        Toast('两次密码输入不一致')
        done(false);
        return
      }
      
      try {
        // AES 加密密码
        const encryptedPassword = this.encryptPassword(newPassword)
        await changePassword({
          mobile: this.loginInfo.mobile, smsCode: smsCode, password: encryptedPassword
        })
        Toast('密码修改成功')
        this.showPasswordDialog = false
      } catch (err) {
        Toast(err.message || '密码修改失败')
        done(false);
      }
    },
    /**
     * AES-ECB-PKCS7Padding 加密方法
     * @param {string} text - 明文密码
     * @returns {string} Base64 编码后的密文
     */
    encryptPassword(text) {
      const SECRET_KEY = CryptoJS.enc.Utf8.parse('zp_base_aes_2026')
      const wordArray = CryptoJS.enc.Utf8.parse(text)
      const encrypted = CryptoJS.AES.encrypt(wordArray, SECRET_KEY, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      })
      return encrypted.toString()
    },
    // 验证码
    async handleSendMobileCode() {
      if (this.countdown > 0) return
      
      // 先检查图形验证码输入框
      if (!this.mobileForm.captchaCode) {
        Toast('请输入图形验证码'); return
      }
      try {
        await request.post('/api/company/getMessage', {
          mobile: null, areaCode: '86',
          captchaCode: this.mobileForm.captchaCode,
          uuid: this.mobileForm.uuid
        })
        this.startCountdown()
        Toast('验证码已发送')
        this.loadBindingCaptcha() // 刷新图片验证码
      } catch (err) {
        Toast(err.message || '验证码发送失败')
      }
    },
    async handleSendPasswordCode() {
      if (this.passwordCountdown > 0) return
      
      // 先检查图形验证码输入框
      if (!this.passwordForm.captchaInput) {
        Toast('请输入图形验证码'); return
      }
      try {
        await request.post('/api/company/getMessage', {
          mobile: null, areaCode: '86',
          captchaCode: this.passwordForm.captchaInput,
          uuid: this.passwordCaptchaUuid
        })
        this.startPasswordCountdown()
        Toast('验证码已发送')
        this.loadPasswordCaptcha() // 刷新图片验证码
      } catch (err) {
        Toast(err.message || '验证码发送失败')
      }
    },
    // 加载图形验证码
    loadBindingCaptcha() {
      request.get('/api/captchaImage').then(res => {
        if (res.code === 200) {
          this.bindingCaptchaUrl = `data:image/png;base64,${res.img}`
          this.mobileForm.uuid = res.uuid || ''
        }
      }).catch(err => {
        console.error('获取验证码失败:', err)
      })
    },
    // 刷新图形验证码
    refreshBindingCaptcha() {
      this.loadBindingCaptcha()
    },
    // 加载密码图形验证码
    loadPasswordCaptcha() {
      request.get('/api/captchaImage').then(res => {
        if (res.code === 200) {
          this.passwordCaptchaUrl = `data:image/png;base64,${res.img}`
          this.passwordCaptchaUuid = res.uuid || ''
        }
      }).catch(err => {
        console.error('获取密码验证码失败:', err)
      })
    },
    // 刷新密码图形验证码
    refreshPasswordCaptcha() {
      this.loadPasswordCaptcha()
    },
    startPasswordCountdown() {
      this.passwordCountdown = 60
      this.passwordTimer = setInterval(() => {
        this.passwordCountdown--
        if (this.passwordCountdown <= 0) { clearInterval(this.passwordTimer); this.passwordTimer = null }
      }, 1000)
    },
    startCountdown() {
      this.countdown = 60
      this.timer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) { clearInterval(this.timer); this.timer = null }
      }, 1000)
    },
    clearTimer() {
      if (this.timer) { clearInterval(this.timer); this.timer = null }
    },
    // 企业认证
    openAuthDialog() {
      this.form = {
        ...this.authInfo,
        brands: (this.authInfo.brands || []).map(b => ({ ...b }))
      }
      this.showAuthDialog = true
    },
    /** 行业输入处理：限制10个字符，不能输入特殊字符 */
    handleIndustryInput() {
      let cleaned = this.form.industry.replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '')
      if (cleaned.length > 10) {
        cleaned = cleaned.substring(0, 10)
      }
      this.form.industry = cleaned
    },
    /** 企业名称输入处理：限制20个字符，不能输入特殊字符 */
    handleCompanyNameInput() {
      let cleaned = this.form.companyName.replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, '')
      if (cleaned.length > 20) {
        cleaned = cleaned.substring(0, 20)
      }
      this.form.companyName = cleaned
    },
    /** 法人姓名输入处理：只能输入汉字和英文字母 */
    handleLegalNameInput() {
      let cleaned = this.form.legalName.replace(/[^\u4e00-\u9fa5a-zA-Z]/g, '')
      if (cleaned.length > 20) {
        cleaned = cleaned.substring(0, 20)
      }
      this.form.legalName = cleaned
    },
    /** 法人电话输入处理：只允许数字，限制11位 */
    handleLegalPhoneInput() {
      let cleaned = this.form.legalPhone.replace(/[^0-9]/g, '')
      if (cleaned.length > 11) {
        cleaned = cleaned.substring(0, 11)
      }
      this.form.legalPhone = cleaned
    },
    /** 品牌名称输入处理：限制10个字符，只能输入汉字和英文字母 */
    handleBrandNameInput(index) {
      if (!this.form.brands[index]) return
      let cleaned = this.form.brands[index].name.replace(/[^\u4e00-\u9fa5a-zA-Z]/g, '')
      if (cleaned.length > 10) {
        cleaned = cleaned.substring(0, 10)
      }
      this.form.brands[index].name = cleaned
    },
    async saveAuthInfo() {
      // 验证行业
      if (!this.form.industry || !String(this.form.industry).trim()) {
        Toast('请输入行业'); return
      }
      
      // 验证企业名称
      if (!this.form.companyName || !String(this.form.companyName).trim()) {
        Toast('请输入企业名称'); return
      }
      
      // 验证营业执照
      if (!this.form.license) { Toast('请上传营业执照'); return }
      
      // 验证法人姓名
      if (!this.form.legalName || !String(this.form.legalName).trim()) {
        Toast('请输入法人姓名'); return
      }
      const legalNameReg = /^[\u4e00-\u9fa5a-zA-Z]{2,20}$/
      if (!legalNameReg.test(this.form.legalName)) {
        Toast('法人姓名应为 2-20 个汉字或英文字母'); return
      }
      
      // 验证法人电话
      if (!this.form.legalPhone || !String(this.form.legalPhone).trim()) {
        Toast('请输入法人电话'); return
      }
      if (!/^1[3-9]\d{9}$/.test(this.form.legalPhone)) {
        Toast('法人电话格式不正确'); return
      }
      
      // 验证法人身份证
      if (!this.form.legalIdCardFront || !this.form.legalIdCardBack) {
        Toast('请上传法人身份证正反面'); return
      }
      
      // 验证品牌名称（如果有品牌）
      if (this.form.brands && this.form.brands.length > 0) {
        for (let i = 0; i < this.form.brands.length; i++) {
          if (!this.form.brands[i].name || !String(this.form.brands[i].name).trim()) {
            Toast(`请输入第 ${i + 1} 个品牌名称`); return
          }
        }
      }
      
      const params = {
        industry: this.form.industry,
        companyName: this.form.companyName,
        businessLicenseImage: this.form.license,
        legalPersonName: this.form.legalName,
        legalPersonPhone: this.form.legalPhone,
        legalPersonFrontImage: this.form.legalIdCardFront,
        legalPersonBackImage: this.form.legalIdCardBack,
        companyBrandList: (this.form.brands || []).map(item => {
          const brandItem = { brandName: item.name, brandImage: item.image }
          if (item.id) brandItem.id = item.id
          return brandItem
        })
      }
      try {
        await completeCompany(params)
        this.authInfo = {
          industry: this.form.industry,
          companyName: this.form.companyName,
          license: this.form.license,
          legalName: this.form.legalName,
          legalPhone: this.form.legalPhone,
          legalIdCardFront: this.form.legalIdCardFront,
          legalIdCardBack: this.form.legalIdCardBack,
          brands: (this.form.brands || []).map(b => ({ ...b }))
        }
        this.authStatus = 1
        this.showAuthDialog = false
        Toast('企业认证信息提交成功')
      } catch (err) {
        Toast(err.message || '企业认证信息提交失败')
      }
    },
    // 营业执照
    triggerLicenseUpload() { this.$refs.licenseInput.click() },
    async onLicenseChange(e) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) { Toast('请选择图片文件'); return }
      try {
        const url = await this.uploadFile(file)
        this.form.license = url
      } catch (err) {
        Toast(err.message || '图片上传失败')
      }
    },
    // 身份证
    triggerIdCardUpload(type) {
      const ref = type === 'front' ? 'legalIdCardFrontInput' : 'legalIdCardBackInput'
      this.$refs[ref].click()
    },
    async onIdCardChange(e, type) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) { Toast('请选择图片文件'); return }
      try {
        const url = await this.uploadFile(file)
        const key = type === 'front' ? 'legalIdCardFront' : 'legalIdCardBack'
        this.form[key] = url
      } catch (err) {
        Toast(err.message || '图片上传失败')
      }
    },
    // 品牌
    addBrand() {
      this.form.brands.push({ image: '', name: '' })
    },
    removeBrand(index) {
      this.form.brands.splice(index, 1)
    },
    triggerBrandUpload(index) {
      this.currentBrandIndex = index
      this.$refs.brandInput.click()
    },
    async onBrandChange(e) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) { Toast('请选择图片文件'); return }
      try {
        const url = await this.uploadFile(file)
        if (this.currentBrandIndex >= 0 && this.form.brands[this.currentBrandIndex]) {
          this.form.brands[this.currentBrandIndex].image = url
        }
      } catch (err) {
        Toast(err.message || '图片上传失败')
      }
    }
  }
}
</script>

<style scoped>
.m-company__skeleton {
  min-height: 60vh;
}
.m-company__skeleton-avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0 20px;
  background: #fff;
  margin-bottom: 12px;
}
.m-company__skeleton-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-company-shimmer 1.4s ease infinite;
}
.m-company__skeleton-hint {
  width: 80px;
  height: 14px;
  border-radius: 4px;
  margin-top: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-company-shimmer 1.4s ease infinite;
}
.m-company__skeleton-code {
  width: 120px;
  height: 14px;
  border-radius: 4px;
  margin-top: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-company-shimmer 1.4s ease infinite;
}
.m-company__skeleton-title {
  width: 80px;
  height: 16px;
  border-radius: 4px;
  margin: 16px 16px 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-company-shimmer 1.4s ease infinite;
}
.m-company__skeleton-cell-group {
  margin: 0 12px 12px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}
.m-company__skeleton-cell {
  height: 52px;
  border-bottom: 1px solid #f5f5f5;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: m-company-shimmer 1.4s ease infinite;
}
@keyframes m-company-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.m-company {
  min-height: 100vh;
  background: #F5F7FA;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  padding-bottom: 20px;
}

.m-company__avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0 20px;
  background: #fff;
  margin-bottom: 12px;
}

.m-company__avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #F5FAFF;
  border: 1px solid #E4F2FF;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

/* 只读模式（审核中）- 无点击效果 */
.personal-info-avatar--readonly {
  cursor: default;
}

.m-company__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Logo 遮罩样式 */
.m-company__avatar-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.m-company__avatar:hover .m-company__avatar-mask {
  opacity: 1;
}

.m-company__avatar-mask span {
  color: #FFFFFF;
  font-size: 12px;
}

.m-company__code {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

/* Logo 审核状态提示 - 相对于 Logo 容器定位 */
.personal-avatar-audit-tip {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

/* 审核中 - 蓝色 */
.personal-avatar-tag-pending {
  background: rgba(225, 243, 248, 0.9);
  color: #23B4DC;
}

/* 驳回 - 红色 */
.personal-avatar-tag-rejected {
  background: rgba(255, 241, 240, 0.9);
  color: #FF4D4F;
}

.m-company__section-title {
  font-size: 13px;
  color: #999;
  padding: 16px 16px 8px;
}

.m-company__group {
  margin: 0 12px 12px;
  border-radius: 12px;
  overflow: hidden;
}

.m-company__auth-reason {
  margin: 8px 16px;
  padding: 8px 12px;
  background: #FFF1F0;
  border-radius: 8px;
  font-size: 13px;
  color: #FF4D4F;
}

.m-company__license-preview {
  padding: 8px 16px;
}

.m-company__license-preview img {
  width: 100%;
  border-radius: 8px;
}

.m-company__auth-action {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

/* 弹窗 */
.m-company__dialog-field {
  padding: 16px;
}

.m-company__dialog-content {
  padding: 12px 16px;
}

.m-company__dialog-content .van-field {
  margin-bottom: 8px;
  border-radius: 8px;
  background: #F8FAFC;
}

.m-company__code-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.m-company__code-field {
  flex: 1;
}

.m-company__code-btn {
  flex-shrink: 0;
  height: 44px;
  border-radius: 8px;
  white-space: nowrap;
}

/* 密码提示 */
.m-company__pwd-hint {
  font-size: 12px;
  color: #999;
  margin: -2px 4px 8px;
  line-height: 1.5;
}

/* 认证弹窗 */
.m-company__auth-popup {
  padding-bottom: env(safe-area-inset-bottom);
}

.m-company__auth-popup-title {
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 0;
  border-bottom: 1px solid #ebedf0;
}

.m-company__upload-label {
  font-size: 14px;
  color: #646566;
  margin: 12px 0 8px;
}

.m-company__upload-box {
  width: 100%;
  height: 120px;
  background: #F5FAFF;
  border: 1px dashed #C6E5FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 12px;
}

.m-company__upload-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.m-company__upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #ccc;
}

.m-company__idcard-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.m-company__idcard-box {
  flex: 1;
  height: 90px;
  background: #F5FAFF;
  border: 1px dashed #C6E5FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.m-company__idcard-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.m-company__brands-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 0 8px;
}

.m-company__brand-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.m-company__brand-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.m-company__brand-image {
  width: 56px;
  height: 56px;
  background: #F5FAFF;
  border: 1px dashed #C6E5FF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.m-company__brand-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.m-company__brand-name {
  flex: 1;
  border-radius: 8px;
}
</style>
